import { useState, useMemo } from "react";
import { Button } from "./ui/button";
import { Card, CardContent, CardFooter, CardHeader } from "./ui/card";
import { Checkbox } from "./ui/checkbox";
import { AlertTriangle, Phone, Shield, Lock } from "lucide-react";

const SafetyGate = ({ user, onAccept }) => {
  // Check if user has already accepted - memoize to prevent unnecessary recalculations
  const hasAccepted = useMemo(() => {
    if (!user?.user_id) return null; // null = still loading/unknown
    const stored = localStorage.getItem(`valorlink_safety_accepted_${user.user_id}`);
    return !!stored;
  }, [user?.user_id]);

  const [isChecked, setIsChecked] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  const handleAccept = () => {
    if (!isChecked) return;
    
    // Store acceptance with timestamp in localStorage
    if (user?.user_id) {
      const acceptanceData = JSON.stringify({
        accepted: true,
        timestamp: new Date().toISOString(),
        user_id: user.user_id
      });
      localStorage.setItem(`valorlink_safety_accepted_${user.user_id}`, acceptanceData);
    }
    setIsDismissed(true);
    if (onAccept) onAccept();
  };

  // Don't render if: user not loaded yet, already accepted, or manually dismissed
  if (hasAccepted === null || hasAccepted === true || isDismissed) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4 overflow-y-auto">
      <Card className="w-full max-w-lg bg-white shadow-2xl border-0 my-4">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
            <Shield className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>
            Welcome to VALORLink™
          </h1>
        </CardHeader>
        
        <CardContent className="space-y-4 px-6">
          <p className="text-center text-muted-foreground">
            VALORLink provides <span className="font-semibold text-foreground">peer-to-peer support</span> from trained fellow veterans.
            It <span className="font-semibold text-foreground">does not</span> provide medical, mental health, or clinical services.
          </p>

          {/* Emergency Notice */}
          <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-semibold text-destructive mb-1">If you are in immediate danger:</p>
                <div className="flex flex-col gap-1 text-muted-foreground">
                  <a href="tel:911" className="flex items-center gap-2 hover:text-destructive transition-colors">
                    <Phone className="w-4 h-4" />
                    <span>Contact <strong>911</strong></span>
                  </a>
                  <a href="tel:988" className="flex items-center gap-2 hover:text-destructive transition-colors">
                    <Phone className="w-4 h-4" />
                    <span>Veterans Crisis Hotline: <strong>988</strong>, Press 1</span>
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Privacy Notice */}
          <div className="bg-muted/50 border border-border rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Lock className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" />
              <div className="text-sm text-muted-foreground">
                <p className="font-semibold text-foreground mb-1">Your Privacy</p>
                <ul className="space-y-1">
                  <li>• VALORLink collects only information needed to provide peer support and platform safety.</li>
                  <li>• We do not sell user data.</li>
                  <li>• Information may be reviewed if there is a safety concern or incident.</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Acknowledgment Checkbox */}
          <div className="flex items-start gap-3 p-3 bg-primary/5 rounded-lg border border-primary/20">
            <Checkbox 
              id="acknowledge" 
              checked={isChecked}
              onCheckedChange={setIsChecked}
              className="mt-0.5"
              data-testid="safety-gate-checkbox"
            />
            <label 
              htmlFor="acknowledge" 
              className="text-sm text-foreground cursor-pointer leading-relaxed"
            >
              I acknowledge and agree that VALORLink provides peer support only, not medical, mental health, or emergency services. I understand I should contact 911 or 988 if in immediate danger.
            </label>
          </div>
        </CardContent>

        <CardFooter className="flex flex-col gap-3 px-6 pb-6">
          <Button 
            onClick={handleAccept}
            disabled={!isChecked}
            className="w-full bg-primary hover:bg-primary/90 text-white font-semibold py-6 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="safety-gate-accept-btn"
          >
            I Understand & Accept
          </Button>
          <p className="text-xs text-center text-muted-foreground">
            This acknowledgment is required to access VALORLink services
          </p>
        </CardFooter>
      </Card>
    </div>
  );
};

export default SafetyGate;
